import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Phone, MessageCircle, MapPin, Clock, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-cream">
      <Header />
      
      {/* Page Header */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-burgundy to-burgundy-dark text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Nous sommes à votre écoute pour répondre à toutes vos questions
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {/* WhatsApp Card */}
              <Card className="p-8 bg-white border-2 border-green-100 hover:border-green-300 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-4 bg-green-100 rounded-2xl">
                    <MessageCircle className="text-green-600" size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
                    <p className="text-gray-600 mb-4">
                      Commande rapide et assistance instantanée
                    </p>
                    <a 
                      href="https://wa.me/221781270846?text=Bonjour,%20je%20souhaite%20des%20informations"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-green-600 hover:bg-green-700 text-white gap-2">
                        <MessageCircle size={18} />
                        Ouvrir WhatsApp
                      </Button>
                    </a>
                  </div>
                </div>
              </Card>

              {/* Phone Card */}
              <Card className="p-8 bg-white border-2 border-burgundy/20 hover:border-burgundy/40 hover:shadow-xl transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-4 bg-burgundy/10 rounded-2xl">
                    <Phone className="text-burgundy" size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Téléphone</h3>
                    <p className="text-gray-600 mb-4">
                      Disponible aux heures d'ouverture
                    </p>
                    <div className="space-y-2">
                      <a href="tel:+221781270846" className="block">
                        <Button variant="outline" className="w-full border-burgundy text-burgundy hover:bg-burgundy hover:text-white gap-2 transition-all duration-300">
                          <Phone size={18} />
                          +221 78 127 08 46
                        </Button>
                      </a>
                      <a href="tel:+221779093914" className="block">
                        <Button variant="outline" className="w-full border-burgundy text-burgundy hover:bg-burgundy hover:text-white gap-2 transition-all duration-300">
                          <Phone size={18} />
                          +221 77 909 39 14
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Contact Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* Location */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl">
                    <MapPin className="text-burgundy" size={28} />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Localisation</h3>
                <p className="text-gray-600">
                  Thiès, Sénégal
                </p>
              </Card>

              {/* Email */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl">
                    <Mail className="text-burgundy" size={28} />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
                <a 
                  href="mailto:sales@volailleprime.com" 
                  className="text-burgundy hover:underline break-words"
                >
                  sales@volailleprime.com
                </a>
              </Card>

              {/* Hours */}
              <Card className="p-6 bg-white border-2 border-gray-100 hover:border-burgundy/30 hover:shadow-lg transition-all duration-300 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="p-4 bg-burgundy/10 rounded-2xl">
                    <Clock className="text-burgundy" size={28} />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Horaires</h3>
                <p className="text-gray-600 text-sm">
                  Selon disponibilité<br />de production
                </p>
              </Card>
            </div>

            {/* Additional Info */}
            <Card className="p-8 bg-gradient-to-br from-burgundy/5 to-gold/5 border-2 border-burgundy/20">
              <h3 className="text-2xl font-bold text-burgundy mb-4 text-center">
                Jours et horaires de commande
              </h3>
              <p className="text-center text-gray-700 mb-4">
                Nos horaires varient selon la disponibilité de production et les cycles d'élevage.
              </p>
              <p className="text-center text-gray-600">
                <strong className="text-burgundy">Conseil :</strong> Contactez-nous à l'avance pour planifier 
                votre commande et garantir la disponibilité.
              </p>
            </Card>

            {/* Map Placeholder */}
            <div className="mt-12">
              <Card className="overflow-hidden">
                <div className="relative h-96 bg-gradient-to-br from-burgundy/10 to-gold/10 flex items-center justify-center">
                  <div className="text-center p-8">
                    <MapPin className="mx-auto mb-4 text-burgundy" size={48} />
                    <h3 className="text-2xl font-bold text-burgundy mb-2">Thiès, Sénégal</h3>
                    <p className="text-gray-600">
                      Production locale pour une fraîcheur garantie
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;